import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCanvasDocStore, inFlightOpsAffectExport } from "./store/canvas-doc-store";
import type { CanvasMetaState } from "./store/canvas-doc-store";
import { useCanvasApi } from "../../lib/canvas-api";
import { useOgImageResolver } from "../../lib/og-image-source";
import type { LinkCardImageResolver } from "../../lib/canvas-raster-images";
import { renderCanvasExportPdfBlob } from "../../lib/canvas-pdf-snapshot";
import { getWebConfig } from "../../env";
import { EXPORT_MAX_BYTES, formatMb } from "./upload-limits";
import {
  isDrawableObject,
  normalizeCanvasSizePreset,
  resolveCanvasRect,
  EXPORT_UPLOAD_ID_METADATA_HEADER,
  type CanvasFrame,
  type CanvasObject,
} from "@refinement-canvas/domain";

// 全体像 PDF はサムネより生成・転送コストが高いため、サムネ（2.5 秒）より長めにデバウンスする。
const UPLOAD_DEBOUNCE_MS = 20_000;

/** 全体像 PDF へ実際に描画されるオブジェクト（comment-thread は描画しない）だけを残す。 */
function drawableObjects(objects: readonly CanvasObject[]): readonly CanvasObject[] {
  // canvas-pdf-export / canvas-thumbnail / api の hasDrawableObject と同じ描画対象判定を共有正本
  // （@refinement-canvas/domain の isDrawableObject）から参照する。これに合わせないと、コメントのみの
  // キャンバスで空白 PDF を保存してしまう。
  return objects.filter((object) => isDrawableObject(object.type));
}

/**
 * 編集中のキャンバスから、全体像 PDF スナップショットを約 20 秒のデバウンスで S3 へ自動保存する。
 * サムネイル自動保存（useCanvasThumbnail）と同型だが、別タイマー・別世代で独立に動作する。
 * Connect Note は保存された PDF を canvas-snapshot 経由で取得して表示する。
 * options.enabled が false の間は自動保存を起動せず、予約済みのタイマーと進行中の保存の世代を止める（完全削除の
 * 処理中・済みと分かったキャンバスでは、保存をサーバが 410 で拒否し、失敗のたびに再試行し続けるため）。
 */
export function useCanvasExportSnapshot(
  canvasId: string,
  options: { readonly enabled?: boolean | undefined } = {}
): void {
  const enabled = options.enabled ?? true;
  const activeCanvasId = useCanvasDocStore((s) => s.canvasId);
  // objects は effect の依存に入れない（購読しない）。依存に入れると、共同編集の受信（onServerCommit）や
  // 継続編集で objects 参照が変わるたびに自動保存のデバウンスがリセットされ、編集が活発な局面ほど 20 秒
  // タイマーが一度も発火せず全体像が保存されないタイマー飢餓に陥る。保存対象は発火時点で getState() から読む。
  const updateLocalMeta = useCanvasDocStore((s) => s.updateLocalMeta);
  // このタブのローカル編集で dirty になったときだけ保存する。共同編集の受信専用タブ（他者編集を
  // 反映しているだけのタブ）や、初回 bootstrap・再接続/resync 直後の状態反映では保存しない
  // （観測者タブの劣化 PDF で current を上書きしないため）。
  const exportSnapshotDirty = useCanvasDocStore((s) => s.exportSnapshotDirty);
  // 初回 stale bootstrap 無視後の resync 待ち中は、committed / objects が権威スナップショット未取り込みの
  // 乖離状態であり得るため、自動保存を起動しない（resync 完了の loadSnapshot 全適用でフラグが下りる）。
  const resyncPending = useCanvasDocStore((s) => s.resyncPending);
  const isTrashed = useCanvasDocStore((s) => s.meta.isTrashed ?? false);
  const clearExportSnapshotDirty = useCanvasDocStore((s) => s.clearExportSnapshotDirty);
  const canvasApi = useCanvasApi();
  // リンクカードの画像は同一オリジンの中継経由でしか読めないため、解決関数を描画へ渡す。
  const resolveLinkCardImageUrl = useOgImageResolver();
  // 全体像 PDF の保存（POST /api/canvases/:id/export）と exportKey の更新は canWrite 権限が必要。
  // 閲覧専用ユーザーで自動保存を起動すると 20 秒ごとに 403 を出し続けるため、書き込み権限が無ければ
  // 保存もクリアも行わない。/me クエリは他コンポーネントとキャッシュ共有される。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: canvasApi.fetchMe });
  const canWrite = meQuery.data?.actor.permissions.canWrite ?? false;
  const uploadTimerRef = useRef<number | undefined>(undefined);
  const uploadGenerationRef = useRef(0);
  // 保存（presign/PUT/finalize）が一時失敗したとき、dirty を保持したまま effect を再起動して同じデバウンスで
  // 再試行するためのトークン。これが無いと、失敗後に objects も dirty も変わらず effect が再実行されないため、
  // 次のローカル編集まで自動保存が止まる（瞬断で Connect Note 側の全体像が更新されなくなる）。
  const [exportRetryToken, setExportRetryToken] = useState(0);
  // 同一クライアント内で保存（PUT staging → finalize → meta 反映）が時間的に重ならないよう、この Promise
  // チェーンで直列化する。PUT 先は staging 一意キーで配信キー（current）はサーバ finalize の CopyObject でしか
  // 書き換わらないため、配信キー自体の巻き戻りはサーバ側（serverSeq ゲート・昇格後の版取り消し）で防ぐが、
  // クライアント内では presign/PUT/finalize と meta 反映の取り違え（古い完了が新しい meta を上書き）を避けたい。
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    // 無効になったら起動しない（直前の実行のクリーンアップがタイマーを解除し、世代を進めて進行中の保存を止める）。
    if (!enabled) return;
    if (activeCanvasId !== canvasId) return;
    // mock ローカルでは S3 が無いことがあるため、生成・アップロードしない。
    if (getWebConfig().authMode === "mock") return;
    // 閲覧専用（canWrite なし）は保存の入口が 403 になるため、自動保存経路自体を起動しない。
    if (!canWrite) return;
    // ゴミ箱では参照専用のため、全体像 PDF の自動保存を起動しない。
    if (isTrashed) return;
    // 初回 stale bootstrap 無視後の resync 待ち中は起動しない（乖離状態の PDF を保存しない）。
    // resync 完了（loadSnapshot 全適用）でフラグが下りると effect が再実行され、dirty が残っていれば
    // 権威状態ベースで保存が再開される（保存は失われず遅延するだけ）。
    if (resyncPending) return;
    // ローカル編集で dirty になっていなければ保存しない。これにより、初回 bootstrap・再接続/resync 直後の
    // 状態反映や、共同編集の受信専用タブ（他者編集を realtime で反映しているだけ）では保存が起動せず、
    // 回線の遅い観測者タブが描画失敗のプレースホルダ混じり PDF で current を上書きする事故を防ぐ。
    if (!exportSnapshotDirty) return;

    if (uploadTimerRef.current !== undefined) {
      window.clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = undefined;
    }

    // 予約済みタイマーを解除し世代を進める共通クリーンアップ（アンマウント・effect 再実行時に呼ぶ）。
    const cleanup = (): void => {
      uploadGenerationRef.current += 1;
      if (uploadTimerRef.current !== undefined) {
        window.clearTimeout(uploadTimerRef.current);
        uploadTimerRef.current = undefined;
      }
    };

    // デバウンスは「dirty への遷移」で 1 度だけ張る（依存に objects を入れない）。発火までに継続編集や
    // 共同編集の受信で objects が変わっても、保存対象は発火時点に getState() で読む最新状態にする。
    // 並行アップロードの古い完了が最新メタを巻き戻さないよう、予約時点の世代だけがメタ更新できるようにする。
    const generation = (uploadGenerationRef.current += 1);
    uploadTimerRef.current = window.setTimeout(() => {
      uploadTimerRef.current = undefined;
      // 発火時点のサーバ権威 + 楽観状態を読む（スケジュール時点ではなく発火時点の最新を保存する）。
      const state = useCanvasDocStore.getState();
      // 発火までに別キャンバスへ切り替わっていたら何もしない。
      if (state.canvasId !== canvasId) return;
      if (state.meta.isTrashed === true) return;
      // 未 ack の drawable 編集（サーバ送信され ack/reject される本物の op）があるうちは、楽観状態を外部配信用
      // PDF として確定しない（後でサーバ reject されると、本体に存在しないオブジェクトを含む PDF が
      // exports/<id>/snapshot.pdf に残り、dirty も下がって次の編集まで直らない）。ack/reject が settle してから
      // 保存できるよう、dirty を保持したまま再試行する。永続化されない本人用 blob preview は inFlightOpsAffectExport
      // が除外するためここでは待たない（公開 URL 未設定環境でも永久ループしない）。打ち切り上限は設けない
      // ＝未確定の楽観状態を「諦めて確定」させない（失われた op では外部 PDF はサーバ確定状態のまま据え置く）。
      if (inFlightOpsAffectExport(state.inFlightOps)) {
        if (generation === uploadGenerationRef.current) setExportRetryToken((t) => t + 1);
        return;
      }
      // 外部配信スナップショットは「サーバ確定状態（committedObjects）」から描く。楽観状態（objects）には、
      // 送信されない本人用ローカル override（画像差し替え中の blob: プレビュー等）が混ざり、別の drawable 編集を
      // 契機に保存が走ると、未公開の blob 画像が描画側で placeholder 化され外部 PDF を劣化させ得る。committed には
      // override は無く、差し替え前の公開済み画像 src が残るため、これを描けば劣化しない。in-flight ゲートが
      // export 影響 op の settle を保証しているため、committed は直近の確定編集（ack 済み）を反映している。
      // 実際に全体像へ描画される drawable（comment-thread を除く）数で空判定する。
      const snapshotObjects = drawableObjects(Object.values(state.committedObjects));
      // 出力矩形もサーバ確定メタ（committedMeta）のサイズから決める（描画対象の committedObjects と整合させる）。
      const snapshotRect = resolveCanvasRect(normalizeCanvasSizePreset(state.committedMeta.sizePreset));
      // 保存対象を読んだ時点の export 編集 seq と、サーバ確定世代 serverSeq。
      // - snapshotSeq: finalize 後に比較し、保存中に新しいローカル drawable 編集（seq 前進。effect 依存から
      //   objects を外した分の取りこぼし防止）が入っていれば再保存する。
      // - snapshotServerSeq: finalize へ渡し、サーバ側が最新世代と比較して、描画後に他者の drawable 編集が確定
      //   していれば confirmed=false で再保存させる（同時編集で「片方の編集だけの PDF」を current 確定したまま
      //   固定化するのを防ぐ。クライアント未受信の確定も取りこぼさないようサーバ側で比較する）。
      const snapshotSeq = state.exportSnapshotDirtySeq;
      const snapshotServerSeq = state.serverSeq;
      if (snapshotObjects.length === 0) {
        // 描画対象が無い空キャンバスでは全体像を保存しない（空白 PDF を保存しない。配信時はサーバ権威で 404）。
        // in-flight ゲートを通過済み＝この空状態はサーバ確定なので、保存すべきローカル編集が無いとみなして
        // dirty を下げる。これにより、次に drawable が追加されたときの dirty 遷移で自動保存が再起動する
        // （objects を依存に入れない代わりに、dirty 遷移で再アームする）。
        if (generation === uploadGenerationRef.current) clearExportSnapshotDirty();
        return;
      }
      // 直前の保存/クリアの完了後に実行し、同一クライアント内の保存（staging への PUT → finalize → メタ更新）を
      // 時間的に重ねない（取り違えで古い完了が新しいメタを上書きするのを防ぐ）。
      uploadChainRef.current = uploadChainRef.current
        .catch(() => undefined)
        .then(() =>
          uploadExportSnapshot(
            canvasApi,
            canvasId,
            snapshotObjects,
            snapshotRect,
            updateLocalMeta,
            () => generation === uploadGenerationRef.current,
            clearExportSnapshotDirty,
            () => setExportRetryToken((t) => t + 1),
            () => useCanvasDocStore.getState().exportSnapshotDirtySeq !== snapshotSeq,
            snapshotServerSeq,
            resolveLinkCardImageUrl
          )
        );
    }, UPLOAD_DEBOUNCE_MS);

    return cleanup;
  }, [
    enabled,
    activeCanvasId,
    canWrite,
    canvasApi,
    canvasId,
    updateLocalMeta,
    exportSnapshotDirty,
    resyncPending,
    isTrashed,
    clearExportSnapshotDirty,
    exportRetryToken,
    resolveLinkCardImageUrl,
  ]);
}

// 単体テスト用に export する（保存完了時の「dirty を下げる / 再アームする」判定の回帰を直接検証するため）。
export async function uploadExportSnapshot(
  canvasApi: ReturnType<typeof useCanvasApi>,
  canvasId: string,
  objects: readonly CanvasObject[],
  canvasRect: CanvasFrame,
  updateLocalMeta: (meta: Partial<CanvasMetaState>) => void,
  isLatest: () => boolean,
  clearExportSnapshotDirty: () => void,
  scheduleRetry: () => void,
  // 保存開始（snapshot 取得）以降に export へ影響する新しいローカル編集が入ったか（exportSnapshotDirtySeq の前進）。
  // effect 依存から objects を外したため、保存中の追加編集はこのコールバックで検知して取りこぼしを防ぐ。
  hasNewerExportEdit: () => boolean,
  // PDF を描画した時点のサーバ確定世代（serverSeq）。finalize へ渡し、サーバ側が現在の最新世代（getCurrentSeq）と
  // 比較して、この PDF が古い世代（描画後に他者の drawable 編集が確定）なら confirmed=false で再保存させる。
  // クライアント側比較だけだと「このタブにまだ未配信の確定」を取りこぼすため（受信遅延中に finalize 完了→
  // clear→後で受信しても再保存されない）、世代比較はサーバ側で行う。
  snapshotServerSeq: number,
  // リンクカードの imageUrl を読み込み可能な URL へ解決する関数（未指定ならその画像を描かない）。
  resolveLinkCardImageUrl?: LinkCardImageResolver | undefined
): Promise<void> {
  try {
    if (!isLatest()) return;
    // 大規模キャンバスでは生成 PDF が上限（EXPORT_MAX_BYTES）を超え得るため、上限を渡して描画幅を段階的に
    // 下げ、上限内へ収めてから保存する。これにより、上限超過のキャンバスでも「古い世代で凍結」させず、
    // 低解像度でも常に最新の全体像へ収束させる（export-url が古い世代を 200 で返し続ける問題を解消する）。
    const blob = await renderCanvasExportPdfBlob(objects, canvasRect, EXPORT_MAX_BYTES, {
      resolveLinkCardImageUrl,
    });
    if (!isLatest()) return;
    if (!blob) {
      // 描画失敗（getContext("2d") / toDataURL 失敗、PNG ヘッダ不一致で renderCanvasExportImage が "" を返す等）。
      // ここで dirty を保持したまま素通しで return すると、タイマー無し・token 不変・objects も effect 依存に
      // 無いため effect が再実行されず、以後のローカル編集でも自動保存が二度と走らなくなる。瞬断と同様に再試行で
      // 回復し得るため、再アームして次のデバウンスで再試行する。
      scheduleRetry();
      return;
    }
    // 最終防衛。下限幅（MIN_EXPORT_WIDTH_PX=1000px）の PNG は最大でも数 MB（1000×562px の RGBA 上限が
    // 約 2.3MB）で EXPORT_MAX_BYTES（16MB）を超えないため、自動縮小は必ず上限内へ収束し、この分岐へは実際には
    // 入らない（＝全体像は常に最新へ収束する）。将来 MIN_EXPORT_WIDTH_PX や上限が変わって超過した場合のみ、
    // 無駄な 413 往復を避けるために保存を打ち切る防御である。
    if (blob.size > EXPORT_MAX_BYTES) {
      console.warn(
        `[export-snapshot] 全体像 PDF が上限（${formatMb(EXPORT_MAX_BYTES)}）を超えたため保存を見送りました（生成サイズ ${formatMb(blob.size)}）。`,
      );
      // !blob と同様、ここで素通しで return すると自動保存が恒久停止する。再アームして次のデバウンスで再試行
      // する（現状到達不能の防御分岐だが、将来到達した場合の停止を防ぐ）。
      scheduleRetry();
      return;
    }
    const presigned = await canvasApi.presignExportUpload({ canvasId, contentLength: blob.size });
    // PUT 直前にも世代を確認し、より新しい保存が予約済みなら古い staging アップロードを作らない
    // （PUT 先は staging 一意キーで、配信用固定キーはサーバ finalize の CopyObject でしか書き換わらない）。
    if (!isLatest()) return;
    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: blob,
      // presign が署名した uploadId を S3 メタデータへ載せる。PUT 先は staging 一意キーだが、finalize 側はこの値も
      // HeadObject.Metadata と照合して「この uploadId の PUT が完了した実体か」を多重防御で確認する。
      headers: {
        "content-type": "application/pdf",
        // ヘッダー名は web/api 共有の正本（@refinement-canvas/domain）を参照し、presign の署名対象キーとの
        // ドリフト（署名不一致で全体像保存がサイレントに全面故障）を防ぐ。
        [EXPORT_UPLOAD_ID_METADATA_HEADER]: presigned.uploadId,
      },
    });
    if (!uploadResponse.ok) {
      throw new Error(`全体像 PDF のアップロードに失敗しました: HTTP ${uploadResponse.status}`);
    }
    // PUT 先は staging 一意キー（exports-staging/<canvasId>/<uploadId>.pdf）で、配信用の固定キー（current）は
    // サーバの finalize（CopyObject）でしか書き換わらない。よって、この時点でより新しい保存が予約済み
    // （!isLatest()）なら、この古い staging を finalize して昇格・確定する必要はない（PUT は配信キーを一切触って
    // いないため、finalize を省いても実体と exportUpdatedAt の不整合は生じない）。昇格・確定は最新世代の保存へ
    // 委ねて中断する（無駄な「stale 昇格→サーバ側で版を取り消し」の往復も避ける）。dirty は最新世代の保存が
    // 下げる／再アームするため、ここでは触らない。
    if (!isLatest()) return;
    // exportKey 確定はサーバの finalize に委ねる（staging を HeadObject で確認し、uploadId が一致＝この PUT の実体で、
    // かつ描画時点の世代が最新のときだけ配信キーへ CopyObject で昇格して exportUpdatedAt を進める）。汎用メタ更新では
    // exportKey を立てられないため、実体の無い exportKey（空の 200 配信）を防げる。
    const result = await canvasApi.finalizeExportUpload({
      canvasId,
      uploadId: presigned.uploadId,
      serverSeq: snapshotServerSeq,
    });
    if (!isLatest()) return;
    if (!result.confirmed) {
      // 今回のアップロードを current として確定できなかった（finalize の世代ゲートまたは昇格後の再確認で stale と
      // 判定され、昇格していない／昇格した版が取り消された）。dirty を保持したまま新しい uploadId で再保存し、最新
      // 状態（受信後の committed）から昇格し直して収束させる。ここで dirty を下げると、stale 据え置きのまま次の
      // ローカル編集まで全体像の更新（Connect Note 側の再描画）が止まってしまう。
      scheduleRetry();
      return;
    }
    updateLocalMeta({ exportKey: result.exportKey });
    // 保存開始（snapshot 取得）以降に export へ影響する新しいローカル編集が入っていれば（exportSnapshotDirtySeq の
    // 前進。サーバ未送信の編集も含む）、この PDF はそれを含まない古い世代なので dirty を保持し再アームする。入って
    // いなければ現在の状態を export 済みにできたので dirty を下げる。なお「他者の編集など、描画後に確定した
    // drawable 変更を含まない古い世代」は、サーバ側で snapshotServerSeq と最新世代を比較して confirmed=false に
    // するため、上の `!result.confirmed` 経路で再アーム済み（クライアント未受信の確定も取りこぼさない）。
    if (hasNewerExportEdit()) {
      scheduleRetry();
    } else {
      clearExportSnapshotDirty();
    }
  } catch (error) {
    console.warn("[export-snapshot] 全体像 PDF の保存に失敗しました", error);
    // 一時失敗（presign/PUT/finalize のネットワーク瞬断等）。dirty は保持したまま（clearExportSnapshotDirty を
    // 呼んでいない）なので、effect を再起動して同じデバウンスで再試行する。世代が最新のときだけ再予約する
    // （保存中により新しい保存が予約済みなら、そちらに任せて二重起動しない）。
    if (isLatest()) {
      scheduleRetry();
    }
  }
}
